/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useMemo } from 'react';
import { makeStyles, Paper, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ExtVideoInfo, MediaStatus } from '../../containers/robot-cloud/RCUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            padding  : theme.spacing(0.1),
            height   : '100%',
            minHeight: 300,
        },

        videoContainer: {
            width     : '100%',
            height    : '100%',
            background: '#000000',
            display   : 'flex',
            alignItems: 'center',
            '&.hidden': {
                display: 'none',
            },
        },

        video: {
            width      : '100%',
            minHeight  : 300,
            '&.preload': {
                display: 'none',
            },
        },

        noVideoMessage: {
            color    : '#ffffff',
            fontSize : 30,
            width    : '100%',
            textAlign: 'center',
        },
    };
});



interface VideoMetricValueDisplayProps {
    readonly className?: string;
    readonly videoClips: readonly ExtVideoInfo[] | undefined;       // the set of smaller video clips to be combined as 1 video
    readonly videoStatus: MediaStatus;
    readonly currentTimeInSec: number;
    readonly videoStartTime: number;                                // the start timestamp of the entire video
    readonly labels: {
        readonly videoNotAvailableMessage: string;
    }
}



/**
 * This component is used for displaying Video from metric
 * @param props
 */
export const VideoMetricValueDisplay = (props: VideoMetricValueDisplayProps) => {

    // sort the video clips by timestamp so it would be easier to find video by current time
    const sortedVideoClips = useMemo(() => {
        if (props.videoClips) {
            return [...props.videoClips].sort((videoInfo1: ExtVideoInfo, videoInfo2: ExtVideoInfo) => {
                return videoInfo1.timestamp - videoInfo2.timestamp;
            });
        }

        return undefined;
    }, [props.videoClips]);


    // We will have 2 video elements which we will alternately display. We use 2 video elements so that we can preload the next clip
    // while another clip is playing.
    // Each Video element has a Source element so we need to keep track of both Video element ans Source element
    const videoRefs = useRef([{
        videoEl : useRef<HTMLVideoElement | null>(null),            // For Video element 1
        sourceEl: useRef<HTMLSourceElement | null>(null),
    }, {
        videoEl : useRef<HTMLVideoElement | null>(null),            // For video element 2
        sourceEl: useRef<HTMLSourceElement | null>(null),
    }]);



    // Find the video clip from the list of sorted vide clips that we should be showing based on props.currentTime
    // If we can't find a video clip that should be showing, get the NEXT clip instead so that we can preload it even though we don't
    // need to show it yet.
    const currentClipIndex = useMemo((): number | undefined => {
        if (sortedVideoClips && sortedVideoClips.length > 0) {
            // convert relative props.currentTime which is in seconds to absolute timestamp
            const currentTimestamp = props.videoStartTime + props.currentTimeInSec * 1000;

            // find and return the first video clip we found that is within the currentTimestamp e.g.
            // currentTimestamp is 00:01:25
            // If videoClip's timestamp = 00:01:00 and length is 30 seconds then this video clip within the currentTimestamp
            const index = sortedVideoClips.findIndex((videoClip: ExtVideoInfo) => {
                return (currentTimestamp >= videoClip.timestamp && currentTimestamp < videoClip.timestamp + videoClip.length)
                        || videoClip.timestamp >= currentTimestamp;
            });
            if (index >= 0) {
                return index;
            }
        }
        return undefined;
    }, [sortedVideoClips, props.currentTimeInSec, props.videoStartTime]);


    // Even though there is currentClipIndex, it doesn't mean we need to show the video because the currentClipIndex might be the clip
    // we want to preload so that we can show later. Therefore, this function will just check if the currentClipIndex need to be shown and set the
    // flag isShowingVideoClip to true/false so we can use it later on in the code without having to call this function multiple time.
    const isShowingVideoClip = useMemo(() => {
        // convert relative props.currentTime which is in seconds to absolute timestamp
        const currentTimestamp = props.videoStartTime + props.currentTimeInSec * 1000;
        if (currentClipIndex !== undefined && sortedVideoClips) {
            const currentClip = sortedVideoClips[currentClipIndex];
            return currentClip.timestamp <= currentTimestamp && currentClip.timestamp + currentClip.length > currentTimestamp;
        }
        return false;
    }, [sortedVideoClips, currentClipIndex, props.videoStartTime, props.currentTimeInSec]);



    const classes = useStyle();


    /**
     * This useEffect is used for start/stop the video element base on the props.videoStatus
     */
    useEffect(() => {
        if (currentClipIndex !== undefined) {
            const { videoEl } = videoRefs.current[currentClipIndex % 2];
            if (videoEl && videoEl.current) {
                try {
                    if (videoEl.current.readyState > 2) {
                        if (isShowingVideoClip && props.videoStatus === MediaStatus.PLAYING && (videoEl.current.paused || videoEl.current.ended)) {
                            videoEl.current.play();
                        } else if (props.videoStatus !== MediaStatus.PLAYING && !videoEl.current.paused && !videoEl.current.ended) {
                            videoEl.current.pause();
                        }
                    }
                } catch (error) {
                    // ignore error
                }
            }
        }
    }, [videoRefs, props.videoStatus, currentClipIndex, isShowingVideoClip]);



    /**
     * This useEffect is used for swapping the video URL from the 2 video source.
     */
    useEffect(() => {
        if (currentClipIndex !== undefined && sortedVideoClips) {
            const currentClip = sortedVideoClips[currentClipIndex];
            const currentVideoSourceRef = videoRefs.current[currentClipIndex % 2].sourceEl;
            const nextVideoSourceRef = videoRefs.current[(currentClipIndex + 1) % 2].sourceEl;

            // Load the current video if it is not loaded
            if (currentVideoSourceRef && currentVideoSourceRef.current) {
                if (currentVideoSourceRef.current.src !== currentClip.video) {
                    currentVideoSourceRef.current.src = currentClip.video;
                    const currentVideoRef = videoRefs.current[currentClipIndex % 2].videoEl;
                    if (currentVideoRef.current) {
                        currentVideoRef.current.load();
                    }
                }
            }

            // Preload the next video if we still have video to show
            if (nextVideoSourceRef && nextVideoSourceRef.current && currentClipIndex < sortedVideoClips.length - 1) {
                const nextClip: ExtVideoInfo = sortedVideoClips[currentClipIndex + 1];
                if (nextVideoSourceRef.current.src !== nextClip.video) {
                    nextVideoSourceRef.current.src = nextClip.video;
                    const nextVideoRef = videoRefs.current[(currentClipIndex + 1) % 2].videoEl;
                    if (nextVideoRef.current) {
                        nextVideoRef.current.load();
                    }
                }
            }
        }

    }, [props.currentTimeInSec, videoRefs, sortedVideoClips, currentClipIndex]);



    /**
     * This useEffect is used for keeping the video element's current position sync with with player control position. Sometime the user stop the
     * video and then use the slider to move the time forward and this useEffect will make sure the video will jump to the appropriate frame.
     */
    useEffect(() => {
        if (currentClipIndex !== undefined && sortedVideoClips) {
            const currentVideoRef = videoRefs.current[currentClipIndex % 2].videoEl;
            const currentClip = sortedVideoClips[currentClipIndex];
            if (currentVideoRef && currentVideoRef.current) {
                const clipRelativeCurrentTime = (props.videoStartTime + props.currentTimeInSec * 1000 - currentClip.timestamp) / 1000;
                if (Math.abs(clipRelativeCurrentTime - currentVideoRef.current.currentTime) > 0.5) {
                    currentVideoRef.current.currentTime = clipRelativeCurrentTime;
                }
            }
        }
    }, [props.currentTimeInSec, sortedVideoClips, currentClipIndex, videoRefs, props.videoStartTime]);


    return (
        <Paper className={clsx(classes.root, props.className)}>
            {currentClipIndex !== undefined && (
                <div className={clsx(classes.videoContainer, !isShowingVideoClip && 'hidden')}>
                    <video
                        ref={videoRefs.current[0].videoEl}
                        controls={false}
                        className={classes.video}
                        preload="auto"
                        hidden={currentClipIndex % 2 === 1}
                    >
                        <source ref={videoRefs.current[0].sourceEl} />
                        <track />
                    </video>

                    <video
                        ref={videoRefs.current[1].videoEl}
                        controls={false}
                        className={clsx(classes.video)}
                        preload="auto"
                        hidden={currentClipIndex % 2 === 0}
                    >
                        <source ref={videoRefs.current[1].sourceEl} />
                        <track />
                    </video>
                </div>
            )}

            {!isShowingVideoClip && (
                <div className={classes.videoContainer}>
                    <div className={classes.noVideoMessage}>
                        {props.labels.videoNotAvailableMessage}
                    </div>
                </div>
            )}
        </Paper>
    );
};
